const ELEMENT_NAMES = {
  H: '水素', C: '炭素', N: '窒素', O: '酸素', S: '硫黄', P: 'リン',
  F: 'フッ素', Cl: '塩素', Br: '臭素', I: 'ヨウ素',
}

export default function ScoreOverlay({ score, question, onRetry, onClose, asPanel = false }) {
  if (!score) return null

  const diffEntries = Object.entries(score.elementDiff || {})

  const content = (
    <div
      className={`${asPanel ? 'w-full' : 'max-w-lg w-full mx-4'} rounded-2xl p-6 md:p-8`}
      style={{
        background: 'linear-gradient(145deg, #1a1f35, #111827)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div className="flex justify-center mb-4">
        <span
          className="text-xs px-3 py-1 rounded-full font-bold"
          style={{
            background: score.isCorrect ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)',
            color: score.isCorrect ? '#22c55e' : '#ef4444',
          }}
        >
          {score.isCorrect ? '正解' : '不正解'}
        </span>
      </div>

      <h3 className="text-3xl font-black text-white text-center mb-1">
        {score.isCorrect ? '正解です！' : 'おしい！'}
      </h3>
      <p className="text-center text-white/40 text-sm mb-4">スコア {score.score} / 100</p>

      {/* 判定内訳 */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div
          className="p-3 rounded-lg text-center"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="text-[10px] text-white/45 mb-1">分子式（原子の種類・数）</p>
          <p className="text-sm font-bold" style={{ color: score.formulaMatch ? '#22c55e' : '#f87171' }}>
            {score.formulaMatch ? '一致' : '不一致'}
          </p>
        </div>
        <div
          className="p-3 rounded-lg text-center"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="text-[10px] text-white/45 mb-1">構造（つながり方）</p>
          <p className="text-sm font-bold" style={{ color: score.structureMatch ? '#22c55e' : '#f87171' }}>
            {score.structureMatch ? '一致' : '不一致'}
          </p>
        </div>
      </div>

      {/* あなたの分子式 vs 正解 */}
      <div
        className="p-3 rounded-lg mb-3 flex items-center justify-around"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="text-center">
          <p className="text-[10px] text-white/45 mb-0.5">あなたの分子式</p>
          <p className="text-base font-bold text-white/85">{score.formulaUser || '—'}</p>
        </div>
        <span className="text-white/30">→</span>
        <div className="text-center">
          <p className="text-[10px] text-white/45 mb-0.5">正解の分子式</p>
          <p className="text-base font-bold" style={{ color: '#34D399' }}>{score.formulaAnswer}</p>
        </div>
      </div>

      {/* 過不足フィードバック */}
      {!score.isCorrect && diffEntries.length > 0 && (
        <div
          className="p-3 rounded-lg mb-3"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <p className="text-[11px] font-bold text-white/60 mb-1.5">原子の過不足</p>
          <div className="flex flex-wrap gap-1.5">
            {diffEntries.map(([el, diff]) => (
              <span
                key={el}
                className="text-[11px] px-2 py-0.5 rounded-full"
                style={{
                  background: diff > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
                  color: diff > 0 ? '#fca5a5' : '#93c5fd',
                }}
              >
                {ELEMENT_NAMES[el] || el}
                {diff > 0 ? ` が ${diff} 個多い` : ` が ${-diff} 個足りない`}
              </span>
            ))}
          </div>
        </div>
      )}
      {!score.isCorrect && diffEntries.length === 0 && (
        <div
          className="p-3 rounded-lg mb-3"
          style={{ background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.2)' }}
        >
          <p className="text-[11px] text-white/70">
            原子の数は合っています。結合の種類（単／二重／三重）やつなぎ方を見直してみましょう。
          </p>
        </div>
      )}

      {/* 解説 */}
      <div
        className="p-4 rounded-lg"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <p className="text-xs font-bold text-white/50 mb-2">解説</p>
        <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">
          {question.explanation}
        </p>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={onRetry}
          className="flex-1 py-3 rounded-lg font-bold text-sm text-white"
          style={{ background: 'linear-gradient(135deg, #34D399, #14B8A6)' }}
        >
          もう一度挑戦
        </button>
        <button
          onClick={onClose}
          className="px-5 py-3 rounded-lg font-bold text-sm text-white/60"
          style={{ background: 'rgba(255,255,255,0.05)' }}
        >
          閉じる
        </button>
      </div>
    </div>
  )

  if (asPanel) {
    return <div className="p-4">{content}</div>
  }

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)', zIndex: 20 }}
    >
      {content}
    </div>
  )
}
